import React from 'react';
import { TextField } from 'new/elements/TextField';
import styles from './SelectedLocationPathInput.module.scss';
import { ChangeEventHandler, FormEventHandler } from 'react';
import { ReactComponent as CancelIcon } from 'new/components/icons/fi-sr-cross-circle.svg';
import cx from 'classnames';

interface SelectedLocationPathInputProps {
  readOnly?: boolean;
  placeholder?: string;
  onChange: (
    {
      name,
      path,
      displayName,
      displayPath,
    }: {
      name: string;
      path: string;
      displayName: string;
      displayPath: string;
    },
    nodeId?: string,
  ) => void;
  value: string;
  onSubmit?: () => void;
}

type HandleChange = ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> &
  FormEventHandler<HTMLLabelElement>;

const SelectedLocationPathInput = ({
  onChange,
  readOnly,
  placeholder = 'Select a directory below or type a path here',
  value = '',
  onSubmit,
}: SelectedLocationPathInputProps) => {
  const isReadOnly = Boolean(readOnly);
  const handleChange: HandleChange = (event) => {
    event?.preventDefault();
    const value = event.target.value;

    if (value === '') {
      onChange(null);
      return;
    }

    onChange(
      {
        name: value,
        path: value,
        displayName: value,
        displayPath: value,
      },
      value,
    );

    return;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <TextField
      className={cx({
        [styles.inputWrapper_disabled]: isReadOnly,
      })}
      readOnly={isReadOnly}
      inputProps={{
        ['data-testid']: 'location-path-input',
      }}
      InputProps={{
        style: {
          color: 'var(--color-greyscale-800)',
          fontSize: '0.875rem',
          lineHeight: '1.5rem',
        },
        endAdornment: !isReadOnly ? (
          <CancelIcon
            style={{
              color: 'var(--color-greyscale-200)',
              cursor: 'pointer',
              visibility: value ? 'visible' : 'hidden',
            }}
            onClick={() => {
              onChange(null, value);
            }}
          />
        ) : undefined,
      }}
      fullWidth
      placeholder={placeholder}
      name="locationPath"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      value={value}
    />
  );
};

export default React.memo(SelectedLocationPathInput);
